/**
 * Chart geometry helpers (kit `lib/chart-math` equivalent).
 *
 * Pure plotting arithmetic — scales, "nice" axis maxima and tick formatting. §12.5 allows this:
 * plotting is rendering, and no derived figure ever leaves the SVG.
 */

/**
 * Smallest "nice" number at or above `value`.
 *
 * The step ladder is deliberately fine (worst-case headroom ~33%, usually less): the coarse
 * 1/2/2.5/5 ladder rounded a 260-max line series up to 500 and squashed it into the lower half
 * of the plot. The bar family's own 4× context headroom is applied by the caller, so finer steps
 * make that multiple land consistently near 4× instead of drifting to 8×.
 */
const NICE_STEPS = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];

export function niceMax(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const power = Math.floor(Math.log10(value));
  const base = 10 ** power;
  for (const step of NICE_STEPS) {
    const candidate = step * base;
    if (candidate >= value) return candidate;
  }
  return 10 * base;
}

/** Evenly spaced ticks from 0 to `max` (inclusive), deduplicated for tiny integer scales. */
export function axisTicks(max: number, count = 4): number[] {
  const ticks: number[] = [];
  for (let i = 0; i <= count; i += 1) {
    const value = (max / count) * i;
    // Integer-only ticks when the scale itself is a small integer range (post counts).
    const rounded = max <= 8 ? Math.round(value * 2) / 2 : Math.round(value);
    if (!ticks.includes(rounded)) ticks.push(rounded);
  }
  return ticks;
}

/** Compact tick label: 12 → "12", 1800 → "1.8k", 2500000 → "2.5M". */
export function formatTick(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${trimZero(value / 1_000_000)}M`;
  if (abs >= 1_000) return `${trimZero(value / 1_000)}k`;
  if (Number.isInteger(value)) return String(value);
  return String(Math.round(value * 10) / 10);
}

function trimZero(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}
